/**
 * The one place calendar code turns "tomorrow at 3pm" into an instant.
 *
 * Every calendar path (chat functions, planner, overload/repair analysis,
 * availability, public booking, diagnostics) must compute wall-clock times in
 * the USER's timezone through these helpers. Never use Date#setHours,
 * Date#getDay, new Date("YYYY-MM-DD"), or dayjs without a zone on the server:
 * the server runs in UTC.
 */
import {
  getZonedDayStartUtcMs,
  getZonedParts,
  isValidTimezone,
  zonedTimeToUtcMs,
} from "@smartmanager/shell-ui/shared/time/timezone";

export type CalendarTimezoneSource = "explicit" | "preferences" | "request" | "fallback";

export type CalendarTimezone = {
  timezone: string;
  /** "fallback" means nothing told us the user's zone; callers should say so rather than guess silently. */
  source: CalendarTimezoneSource;
};

const clean = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed && isValidTimezone(trimmed) ? trimmed : null;
};

/**
 * THE server resolver for "what zone is the user in", for every feature, not
 * only calendar. Resolution order: an explicit zone on the request (function
 * args, route body), the user's saved preference, the client's reported zone
 * (header or props), then UTC. Invalid names are skipped, never trusted.
 *
 * Never fall back to the host zone (`Intl...resolvedOptions().timeZone`) or a
 * literal city on a server: the host is UTC on Vercel and Trigger and the
 * developer's own zone in local dev, so that fallback hides the missing
 * preference in exactly the place it would be caught. `source: "fallback"`
 * says nothing told us the zone; copy that names a day or hour can say so.
 */
export function resolveCalendarTimezone(input: {
  explicit?: unknown;
  /** A preferences object, or anything shaped like one; only `timezone` is read. */
  preferences?: unknown;
  requestTimezone?: unknown;
}): CalendarTimezone {
  const explicit = clean(input.explicit);
  if (explicit) return { timezone: explicit, source: "explicit" };
  const preferred = clean(preferenceTimezone(input.preferences));
  if (preferred) return { timezone: preferred, source: "preferences" };
  const request = clean(input.requestTimezone);
  if (request) return { timezone: request, source: "request" };
  return { timezone: "UTC", source: "fallback" };
}

function preferenceTimezone(preferences: unknown): unknown {
  return preferences && typeof preferences === "object"
    ? (preferences as { timezone?: unknown }).timezone
    : undefined;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** "YYYY-MM-DD" of the UTC date containing `utcMs`. */
const utcDayKey = (utcMs: number) => new Date(utcMs).toISOString().slice(0, 10);

/** "YYYY-MM-DD" of the user's local date containing `utcMs`. */
export function zonedDayKey(timezone: string, utcMs: number): string {
  const { year, month, day } = getZonedParts(timezone, utcMs);
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** First instant of the user's local day containing `utcMs`, shifted by `dayOffset` days. */
export function zonedDayStart(timezone: string, utcMs: number, dayOffset = 0): number {
  return getZonedDayStartUtcMs(timezone, utcMs, dayOffset);
}

/**
 * The calendar date a calendar timestamp stands for, or null for an instant.
 * A flagged all-day item at midnight UTC is that UTC date (Google parses
 * `start.date` that way); a flagged item elsewhere, or an unflagged one at the
 * user's local midnight, is the user's local date. An unflagged timestamp at
 * midnight UTC is an instant: 5pm in Los Angeles is 00:00Z the next day.
 */
export function allDayDateKey(timestamp: number, timezone: string, flaggedAllDay: boolean): string | null {
  if (flaggedAllDay && timestamp % DAY_MS === 0) return utcDayKey(timestamp);
  if (flaggedAllDay || zonedDayStart(timezone, timestamp) === timestamp) return zonedDayKey(timezone, timestamp);
  return null;
}

/**
 * The calendar date a todo's due timestamp stands for when the todo carries
 * no time, or null for a timed due. Todos have no all-day flag, so the date
 * is read from the shape the task paths store: a local date sits at the start
 * or the last millisecond of that local day (the Google Tasks sync moves the
 * API's midnight-UTC date there). Midnight UTC alone is not a date: a timed
 * due such as 5:00 PM in Los Angeles lands on 00:00Z.
 */
export function dateOnlyDueKey(timestamp: number, timezone: string): string | null {
  const localDayEdge =
    zonedDayStart(timezone, timestamp) === timestamp || zonedDayStart(timezone, timestamp + 1) === timestamp + 1;
  return localDayEdge ? zonedDayKey(timezone, timestamp) : null;
}

/** A todo's due as the user's own day reads it, for model-facing payloads. */
export type LocalDue = Readonly<{
  /** "YYYY-MM-DD" in the user's zone. */
  localDate: string;
  /** "5:30 PM" in the user's zone; null when the due carries no time. */
  localTime: string | null;
  /**
   * "Friday, October 23 (today)", "Saturday, October 24 at 5:00 PM (tomorrow)".
   * The date is absolute so a replayed payload stays true; the relative word
   * is as of `now`.
   */
  localLabel: string;
}>;

const dateKeyUtc = (key: string) => {
  const [year, month, day] = key.split("-").map(Number);
  return Date.UTC(year, month - 1, day);
};

function describeLocal(at: number, zone: string, now: number, dateOnly: string | null): LocalDue {
  const localDate = dateOnly ?? zonedDayKey(zone, at);
  const localTime = dateOnly ? null : new Intl.DateTimeFormat("en-US", {
    timeZone: zone, hour: "numeric", minute: "2-digit", hour12: true,
  }).format(at);
  const today = zonedDayKey(zone, now);
  const days = Math.round((dateKeyUtc(localDate) - dateKeyUtc(today)) / DAY_MS);
  const date = new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC", weekday: "long", month: "long", day: "numeric",
    ...(localDate.slice(0, 4) === today.slice(0, 4) ? {} : { year: "numeric" }),
  }).format(dateKeyUtc(localDate));
  const relative = days === 0 ? " (today)" : days === 1 ? " (tomorrow)" : days === -1 ? " (yesterday)" : "";
  return { localDate, localTime, localLabel: `${date}${localTime ? ` at ${localTime}` : ""}${relative}` };
}

const zoneOrUtc = (timezone: string) => (isValidTimezone(timezone) ? timezone : "UTC");

/**
 * The due date a person would say, computed in the user's zone. A model given
 * the bare epoch reads it as a UTC date: a Google Task due today in Los
 * Angeles is stored at 23:59:59.999 local, which is already tomorrow in UTC,
 * so "due today" came out as "due tomorrow". Payloads that a model reads
 * carry this next to the epoch. A date-only due (dateOnlyDueKey) has no time.
 */
export function describeLocalDue(dueAt: number, timezone: string, now: number): LocalDue | null {
  if (!Number.isFinite(dueAt) || !Number.isFinite(now)) return null;
  const zone = zoneOrUtc(timezone);
  return describeLocal(dueAt, zone, now, dateOnlyDueKey(dueAt, zone));
}

/** An instant (a meeting start, a message sent) as the user's clock reads it. */
export function describeLocalInstant(at: number, timezone: string, now: number): LocalDue | null {
  if (!Number.isFinite(at) || !Number.isFinite(now)) return null;
  return describeLocal(at, zoneOrUtc(timezone), now, null);
}

/** ISO weekday of the user's local date: 0 = Monday … 6 = Sunday. */
export function zonedIsoWeekday(timezone: string, utcMs: number): number {
  const { year, month, day } = getZonedParts(timezone, utcMs);
  return (new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7;
}

/** First instant of the user's local ISO week (Monday) containing `utcMs`. */
export function zonedWeekStart(timezone: string, utcMs: number): number {
  return zonedDayStart(timezone, utcMs, -zonedIsoWeekday(timezone, utcMs));
}

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_RE = /^(\d{1,2})(?::(\d{2}))?(?::(\d{2}))?\s*([ap]\.?m\.?)?$/i;

/** "YYYY-MM-DD" → parts, or null. Rejects impossible dates (2026-02-30). */
export function parseDateKey(value: unknown): { year: number; month: number; day: number } | null {
  const match = typeof value === "string" ? DATE_RE.exec(value.trim()) : null;
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const probe = new Date(Date.UTC(year, month - 1, day));
  if (probe.getUTCFullYear() !== year || probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== day) return null;
  return { year, month, day };
}

/**
 * "3pm", "3:30 PM", "15:30", "09:00:00" → { hour, minute }, or null.
 * Returns null for anything it cannot read exactly; callers must surface that
 * (ask, or refuse) instead of falling back to an auto-scheduled time.
 */
export function parseTimeOfDay(value: unknown): { hour: number; minute: number } | null {
  const match = typeof value === "string" ? TIME_RE.exec(value.trim()) : null;
  if (!match) return null;
  let hour = Number(match[1]);
  const minute = match[2] === undefined ? 0 : Number(match[2]);
  const meridiem = match[4]?.toLowerCase().replace(/\./g, "");
  if (minute > 59) return null;
  if (meridiem) {
    if (hour < 1 || hour > 12) return null;
    hour = (hour % 12) + (meridiem === "pm" ? 12 : 0);
  } else if (hour > 23) {
    return null;
  }
  return { hour, minute };
}

/** Instant of a local date + time in the user's zone, or null when either part is unreadable. */
export function zonedDateTime(timezone: string, date: unknown, time: unknown): number | null {
  const day = parseDateKey(date);
  const clock = parseTimeOfDay(time);
  if (!day || !clock) return null;
  return zonedTimeToUtcMs(timezone, { ...day, ...clock });
}
